import React, { useState } from 'react';
import { ui } from './ui';
import QuestionEditor from './QuestionEditor';
import PaperSheet from './PaperSheet';
import usePaperOps from './usePaperOps';
import { SHEET_W } from './sheetStyles';
import { downloadSheetPdf, printSheet, safeFileName } from './exportUtils';
import { savePaper, logDownload, errMsg } from './aiPaperApi';
import { paperMarks, sectionMarks, SECTION_TITLES } from './aiPaperUtils';

const LOGO = '/logo.png';

function EditorStep({ user, paper, setPaper, warnings }) {
  const [msg, setMsg] = useState('');
  const [exporting, setExporting] = useState('');
  const [view, setView] = useState('question'); // preview mode
  const ops = usePaperOps(user, paper, setPaper, setMsg);

  const total = paperMarks(paper);
  const max = Number(paper.maxMarks) || 0;
  const mismatch = max !== total;

  const exportPdf = async (mode) => {
    setExporting(mode);
    setMsg('');
    try {
      const name = `${safeFileName(paper.subject)}_Class${safeFileName(paper.class)}_${mode === 'key' ? 'ANSWER_KEY' : 'Question_Paper'}.pdf`;
      await downloadSheetPdf(document.getElementById(`aip-export-${mode}`), name);
      if (paper._id) logDownload(user, paper._id, mode);
    } catch (e) { setMsg('❌ PDF failed: ' + e.message); }
    finally { setExporting(''); }
  };

  const doPrint = async () => {
    try {
      await printSheet(document.getElementById(`aip-export-${view}`), `${paper.subject} ${view === 'key' ? 'Answer Key' : 'Question Paper'}`);
      if (paper._id) logDownload(user, paper._id, view);
    } catch (e) { setMsg('❌ ' + e.message); }
  };

  const save = async () => {
    try {
      const saved = await savePaper(user, paper);
      setPaper((p) => ({ ...p, _id: saved._id }));
      setMsg('✅ Paper saved');
    } catch (e) { setMsg('❌ ' + errMsg(e)); }
  };

  let no = 0;
  return (
    <div>
      <div style={mismatch ? { ...ui.warnBox, background: '#fef2f2', border: '2px solid #ef4444', color: '#991b1b' } : ui.okBox}>
        {mismatch
          ? `⚠️ Marks mismatch: assigned ${total} ≠ maximum ${max} (${total > max ? 'over' : 'short'} by ${Math.abs(total - max)}). Adjust marks, delete or regenerate questions.`
          : `✅ Marks balanced: assigned ${total} = maximum ${max}`}
        <div style={{ fontSize: 12, marginTop: 3, fontWeight: 600 }}>
          {paper.sections.map((s) => `Section ${s.name}: ${sectionMarks(s)}`).join(' · ')}
        </div>
      </div>
      {(warnings || []).map((w) => <div key={w} style={{ ...ui.warnText, marginTop: 4 }}>⚠️ {w}</div>)}
      {msg && <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700 }}>{msg}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0' }}>
        <button style={ui.btnPrimary} disabled={!!exporting} onClick={() => exportPdf('question')}>
          {exporting === 'question' ? 'Preparing…' : '⬇️ Download Question Paper PDF'}
        </button>
        <button style={{ ...ui.btnPrimary, background: 'linear-gradient(135deg,#b91c1c,#dc2626)' }} disabled={!!exporting} onClick={() => exportPdf('key')}>
          {exporting === 'key' ? 'Preparing…' : '🔒 Download Teacher Answer Key PDF'}
        </button>
        <button style={ui.btnGhost} onClick={doPrint}>🖨️ Print {view === 'key' ? 'Answer Key' : 'Question Paper'}</button>
        <button style={ui.btnGhost} onClick={save}>💾 Save</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 16, alignItems: 'start' }}>
        <div style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: 4 }}>
          {paper.sections.map((s) => (
            <div key={s.name}>
              <div style={{ fontWeight: 800, color: '#fff', background: '#4338ca', padding: '6px 10px', borderRadius: 8, margin: '4px 0 8px' }}>
                Section {s.name} — {SECTION_TITLES[s.name]} · {sectionMarks(s)} marks
                {s.attemptAny > 0 && s.attemptAny < s.questions.length ? ` · attempt any ${s.attemptAny} of ${s.questions.length}` : ''}
              </div>
              {s.questions.map((q, i) => (
                <QuestionEditor key={q.qId} q={q} no={++no} mode={paper.languageMode}
                  isFirst={i === 0} isLast={i === s.questions.length - 1} busy={ops.busyId === q.qId}
                  onChange={ops.onChange} onOptionChange={ops.onOptionChange} onMove={ops.onMove}
                  onRegenerate={ops.onRegenerate} onDelete={ops.onDelete} />
              ))}
            </div>
          ))}
        </div>

        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <button style={{ ...ui.btnSmall, background: view === 'question' ? '#e0e7ff' : '#fff' }} onClick={() => setView('question')}>Student paper</button>
            <button style={{ ...ui.btnSmall, background: view === 'key' ? '#fee2e2' : '#fff' }} onClick={() => setView('key')}>Answer key</button>
          </div>
          <div style={{ overflow: 'auto', maxHeight: '75vh', background: '#94a3b8', padding: 10, borderRadius: 8 }}>
            <div style={{ width: SHEET_W * 0.78, overflow: 'hidden' }}>
              <div style={{ transform: 'scale(0.78)', transformOrigin: 'top left', width: SHEET_W }}>
                <PaperSheet paper={paper} mode={view} logoSrc={LOGO} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden full-size sheets used only for PDF export / print (never scaled) */}
      <div style={{ position: 'absolute', left: -99999, top: 0, width: SHEET_W }} aria-hidden="true">
        <PaperSheet id="aip-export-question" paper={paper} mode="question" logoSrc={LOGO} />
        <PaperSheet id="aip-export-key" paper={paper} mode="key" logoSrc={LOGO} />
      </div>
    </div>
  );
}

export default EditorStep;
